import { IMessage } from '@stomp/stompjs';
import type { InfiniteData } from '@tanstack/react-query';
import { atom } from 'jotai';
import { queryClientAtom } from 'jotai-tanstack-query';
import { i18n } from 'next-i18next';
import { switchMap } from 'rxjs';
import type { DTOMessageThreadInfo } from 'src/apis/dto/DTOMessageThreadInfo';
import StarterPackageNudgingModal from 'src/components/StarterPackageNudgingModal';
import { ContentsMatchApiList } from 'src/hooks/useContentsMatchQuery';
import { DM_QUERY_KEYS, MessageListPage } from 'src/hooks/useDirectMessageQuery';
import { HEADER_BANNERS_QUERY_KEYS } from 'src/hooks/useHeaderBannersQuery';
import { setServersInfoAtom, stompBrokerInfoAtom } from 'src/stores/auth/atoms';
import { getInventoryAtom, userInventoryAtom } from 'src/stores/auth/atoms';
import { showModalAtom } from 'src/stores/modal/atoms';
import { isPurchasedStarterPackageAtom } from 'src/stores/payments/atoms';
import { isStarterPackageBannerShownAtom } from 'src/stores/shop/atoms';
import { accumulateSubscriptionHistoryAtom } from 'src/stores/subscription/atoms';
import { getVipRatingDetailsAtom } from 'src/stores/vip/atoms';
import { StompBrokerInfo } from 'src/types/AzarUser';
import { BrokerMessage } from 'src/types/brokerMessage';
import { ModalType } from 'src/types/Modal';
import { Platform } from 'src/types/Platform';
import { atomWithPipes } from 'src/utils/atom';
import { parseRawInventoryItems } from 'src/utils/itemInventory';
import { optimisticUpdaterFactory } from 'src/utils/query';
import { MessageBrokerClient } from 'src/utils/stomp';
export const lastStompMessageAtom = atom<{
  message: string;
  timestamp: number;
} | null>(null);
export const stompCreatedLogAtom = atom<string>('');
export const stompAtom = atom<MessageBrokerClient | null>(null);
export const closeStompAtom = atom(null, (get, set) => {
  const stompClient = get(stompAtom);
  if (stompClient) {
    stompClient.dispose();
    set(stompCreatedLogAtom, get(stompCreatedLogAtom) + '-close');
    set(stompAtom, null);
  }
});
export const createStompAtom = atomWithPipes<null, [StompBrokerInfo]>(null, async (get, set, stompBrokerInfo) => {
  set(stompCreatedLogAtom, get(stompCreatedLogAtom) + '-called');
  if (get(stompAtom)) {
    return;
  }
  set(stompCreatedLogAtom, get(stompCreatedLogAtom) + '-set');
  set(stompAtom, new MessageBrokerClient({
    brokerInfo: {
      brokerURL: stompBrokerInfo.uri.replace(/^tls?:\/\//i, 'wss://'),
      connectHeaders: {
        login: stompBrokerInfo.login,
        passcode: stompBrokerInfo.passcode
      }
    },
    renewBrokerInfoFn: async () => {
      set(stompCreatedLogAtom, get(stompCreatedLogAtom) + '-renewBrokerInfoFn');
      await set(setServersInfoAtom);
      const newStompBrokerInfo = get(stompBrokerInfoAtom);
      if (newStompBrokerInfo) {
        return {
          brokerURL: newStompBrokerInfo.uri.replace(/^tls?:\/\//i, 'wss://'),
          connectHeaders: {
            login: newStompBrokerInfo.login,
            passcode: newStompBrokerInfo.passcode
          }
        };
      }
      return null;
    }
  }));
}, [switchMap(x => x())]);
export const handleStompMessageAtom = atom(null, (get, set, data: IMessage) => {
  if (!data.body) {
    return;
  }
  const body = JSON.parse(data.body) as BrokerMessage;
  switch (body.type) {
    case 'inventoryItemUpdated':
      {
        const queryClient = get(queryClientAtom);
        if (get(isPurchasedStarterPackageAtom)) {
          /**
           * 구매 가능 상품, 넛징 등의 갱신을 고려한 관련 API 캐시 제거
           * 서버 구현의 비동기성으로 인해 구매 로직 직후에는 관련 상태 갱신이 정확하지 않음 (타이밍 이슈)
           * -> 따라서 관련 상태가 명확히 갱신된 시점인 inventoryItemUpdated 브로커 메시지를 기준으로 활용
           */
          queryClient.invalidateQueries({
            queryKey: HEADER_BANNERS_QUERY_KEYS.getHeaderBanners()
          });
          queryClient.invalidateQueries({
            queryKey: [ContentsMatchApiList.GetStarterPackagePopup]
          });
        }
        const parsedInventory = parseRawInventoryItems(body.inventoryItems);
        set(userInventoryAtom, parsedInventory);
        set(accumulateSubscriptionHistoryAtom, parsedInventory);
        break;
      }
    case 'updateVipInfo':
      set(getInventoryAtom);
      set(getVipRatingDetailsAtom);
      break;
    case 'messageReceived':
      {
        const {
          messageThreadId,
          messageInfo
        } = body;
        const queryClient = get(queryClientAtom);
        const prevRoomList = queryClient.getQueryData<DTOMessageThreadInfo[]>(DM_QUERY_KEYS.dmRoomList());
        const isNewThread = !prevRoomList?.some(thread => thread.messageThreadId === messageThreadId);
        if (isNewThread) {
          queryClient.invalidateQueries({
            queryKey: DM_QUERY_KEYS.dmRoomList()
          });
        } else {
          queryClient.setQueryData(DM_QUERY_KEYS.dmRoomList(), optimisticUpdaterFactory<DTOMessageThreadInfo[]>(prev => {
            const index = prev.findIndex(thread => thread.messageThreadId === messageThreadId);
            if (index >= 0) {
              prev[index].lastMessage = messageInfo.messageContent;
              prev[index].lastMessageSentByMe = messageInfo.sentByMe;
              prev[index].lastMessageSeqNo = messageInfo.messageSeqNo;
              prev[index].dateLastMessageSent = messageInfo.dateMessageSent;
            }
          }));
        }
        queryClient.setQueryData(DM_QUERY_KEYS.dmRoom(messageThreadId), optimisticUpdaterFactory<DTOMessageThreadInfo>(prev => {
          prev.lastMessage = messageInfo.messageContent;
          prev.lastMessageSeqNo = messageInfo.messageSeqNo;
          prev.lastMessageSentByMe = messageInfo.sentByMe;
          prev.dateLastMessageSent = messageInfo.dateMessageSent;
        }));
        queryClient.setQueryData(DM_QUERY_KEYS.dmRoomMessages(messageThreadId), optimisticUpdaterFactory<InfiniteData<MessageListPage>>(prev => {
          prev.pages[0].messages.push(messageInfo);
        }));
        break;
      }
    case 'messageAcked':
      {
        const {
          messageThreadId,
          lastAckedSeqNo
        } = body;
        const queryClient = get(queryClientAtom);
        queryClient.setQueryData<DTOMessageThreadInfo[]>(DM_QUERY_KEYS.dmRoomList(), optimisticUpdaterFactory<DTOMessageThreadInfo[]>(prev => {
          const index = prev.findIndex(thread => thread.messageThreadId === messageThreadId);
          if (index >= 0) {
            prev[index].messageSeqNoPeerRead = lastAckedSeqNo;
          }
        }));
        queryClient.setQueryData<DTOMessageThreadInfo>(DM_QUERY_KEYS.dmRoom(messageThreadId), optimisticUpdaterFactory<DTOMessageThreadInfo>(prev => {
          prev.messageSeqNoPeerRead = lastAckedSeqNo;
        }));
        break;
      }
    case 'starterPackagePopup':
      {
        const pageBrokerMessage = body;
        if (pageBrokerMessage.platform !== Platform.WebClient) return;

        /**
         * 과도한 넛징 방지를 위해 동일 세션 내에서 스타터패키지 넛징을 노출한 적이 있는 경우 미러 진입 넛징을 하지 않음
         * 서버에서는 브로커 메시지가 날라오지만 클라에서 무시하는 방향으로 처리
         */
        if (get(isStarterPackageBannerShownAtom)) return;
        const {
          singleProductModule,
          refundPolicyModule,
          packageProductModule: starterPackageModule
        } = pageBrokerMessage.sections[0];
        set(showModalAtom, {
          key: ModalType.STARTER_PACKAGE_NUDGING,
          component: () => <StarterPackageNudgingModal starterPackageModule={starterPackageModule} singleProductModule={singleProductModule} refundPolicyModule={refundPolicyModule} description={i18n?.t('popup_web_starterpackage_desc') || ''} isMirrorEnterNudging />
        });
        break;
      }
    default:
      break;
  }
});